import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Paper, Skeleton} from '@mantine/core';
import React, {useCallback} from 'react';
import _ from 'underscore';

import {fetchOnboardingTasks, openSampleEntry, openSampleSequence} from 'actions/OnboardingActions';
import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import {ENTRY_CHECKLIST_KEY, SEQUENCE_CHECKLIST_KEY} from 'onboarding/OnboardingConstants';
import {trackChecklistOpened} from 'onboarding/OnboardingUtils';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

import CardSection from './CardSection';
import {DEFAULT_SECTION_BUTTON_PROPS, HOMEPAGE_SECTION_TEST_COMPONENT} from './HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.WELCOME;

export default function WelcomeSection(): JSX.Element | null {
  useOnMount(() => {
    _.defer(() => {
      if (BenchlingConfig.ENABLE_NUE_MODULES) {
        fetchOnboardingTasks();
      }
    });
  });

  const entryModule = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getNUEModuleState(OnboardingConstants.MODULES.ENTRY), [])
  );
  const sequenceModule = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getNUEModuleState(OnboardingConstants.MODULES.SEQUENCE), [])
  );

  const hasIncompleteEntryChecklist = entryModule != null && !entryModule.completed;
  const hasIncompleteSequenceChecklist = sequenceModule != null && !sequenceModule.completed;

  if (!hasIncompleteSequenceChecklist && !hasIncompleteEntryChecklist) {
    return null;
  }

  return (
    <CardSection
      title="Explore Benchling"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      noBorder={true}
    >
      <Group spacing="md">
        {hasIncompleteSequenceChecklist && (
          <Button
            {...DEFAULT_SECTION_BUTTON_PROPS}
            onClick={() => {
              trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_EXPLORE_MOLECULAR_BIOLOGY_BUTTON);
              trackChecklistOpened(SEQUENCE_CHECKLIST_KEY, 'homepage');
              openSampleSequence();
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'exploreMolecularBiologyButton'})}
          >
            Explore Molecular Biology
          </Button>
        )}
        {hasIncompleteEntryChecklist && (
          <Button
            {...DEFAULT_SECTION_BUTTON_PROPS}
            onClick={() => {
              trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_EXPLORE_NOTEBOOK_BUTTON);
              trackChecklistOpened(ENTRY_CHECKLIST_KEY, 'homepage');
              openSampleEntry();
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'exploreTheNotebookButton'})}
          >
            Explore the Notebook
          </Button>
        )}
      </Group>
    </CardSection>
  );
}

export function WelcomeSectionPlaceholder({proxyCardClassName}: {proxyCardClassName: string}): JSX.Element {
  return (
    <Paper radius="lg" bg="transparent" className={proxyCardClassName}>
      <Group position="apart" mb="md">
        <Skeleton height={10} width="15%" animate={false} />
        <Skeleton height={10} width="15%" animate={false} />
      </Group>
    </Paper>
  );
}
